import { useCallback, useEffect, useRef, useState } from 'react';
import { adsLabels, trackAdsConversion } from '../analytics';
import { ApiError, api, uploadToS3 } from '../api/client';
import type { Job, SiteConfig } from '../api/types';

export type Phase = 'idle' | 'uploading' | 'mastering' | 'ready' | 'failed';

interface MasteringState {
    phase: Phase;
    job: Job | null;
    fileName: string;
    uploadProgress: number;
    totalProgress: number;
    error: string | null;
    start: (file: File) => Promise<void>;
    reset: () => void;
    markPaid: (job: Job) => void;
}

const POLL_INTERVAL_MS = 3000;

/** Validates the file in the browser with the very same rules as the backend. */
export function validateFile(file: File, config: SiteConfig): string | null {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!extension || !config.acceptedFileTypes.includes(extension)) {
        return `This file type is not accepted, accepted file types are: ${config.acceptedFileTypes.join(', ')}`;
    }
    if (file.size <= 0) {
        return 'The file appears to be empty';
    }
    if (file.size > config.maxUploadBytes) {
        return `The file is too large, the limit is ${Math.floor(config.maxUploadBytes / (1024 * 1024))} MB`;
    }
    return null;
}

/**
 * Owns the whole mastering lifecycle: upload to S3, poll the job until the
 * previews are ready, and expose the state the UI renders.
 */
export function useMastering(config: SiteConfig | null): MasteringState {
    const [phase, setPhase] = useState<Phase>('idle');
    const [job, setJob] = useState<Job | null>(null);
    const [fileName, setFileName] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const pollTimer = useRef<number | null>(null);

    const stopPolling = useCallback(() => {
        if (pollTimer.current !== null) {
            window.clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
    }, []);

    useEffect(() => stopPolling, [stopPolling]);

    const poll = useCallback(
        (jobId: string) => {
            stopPolling();
            pollTimer.current = window.setInterval(async () => {
                try {
                    const next = await api.job(jobId);
                    setJob(next);
                    if (next.status === 'ready') {
                        stopPolling();
                        setPhase('ready');
                        // The legacy "mastering finished" micro conversion:
                        // valueless, counted once, useful as an optimisation
                        // signal while the number of sales is still tiny.
                        trackAdsConversion(adsLabels.masteringFinished, {
                            transaction_id: next.jobId,
                        });
                    } else if (next.status === 'failed') {
                        stopPolling();
                        setPhase('failed');
                        setError(
                            next.failureReason ||
                                'The track could not be mastered, please try another file or contact support',
                        );
                    }
                } catch (err) {
                    // Keep polling on transient errors, give up on a 404.
                    if (err instanceof ApiError && err.status === 404) {
                        stopPolling();
                        setPhase('failed');
                        setError(err.message);
                    }
                }
            }, POLL_INTERVAL_MS);
        },
        [stopPolling],
    );

    const start = useCallback(
        async (file: File) => {
            if (!config) {
                return;
            }
            const validationError = validateFile(file, config);
            if (validationError) {
                setPhase('failed');
                setError(validationError);
                return;
            }

            setError(null);
            setFileName(file.name);
            setUploadProgress(0);
            setPhase('uploading');

            try {
                const ticket = await api.createUpload(file.name, file.size);
                setJob(ticket.job);
                await uploadToS3(ticket, file, setUploadProgress);
                setPhase('mastering');
                poll(ticket.jobId);
            } catch (err) {
                setPhase('failed');
                setError(err instanceof Error ? err.message : 'The upload failed, please try again');
            }
        },
        [config, poll],
    );

    const reset = useCallback(() => {
        stopPolling();
        setPhase('idle');
        setJob(null);
        setFileName('');
        setUploadProgress(0);
        setError(null);
    }, [stopPolling]);

    // A single, honest progress value: the upload is the first half, the
    // mastering the second (the legacy UI incremented a fake timer instead).
    const masteringProgress = job && phase !== 'uploading' ? job.progress : 0;
    const totalProgress =
        phase === 'ready'
            ? 100
            : Math.min(99, Math.round(uploadProgress / 2 + masteringProgress / 2));

    return {
        phase,
        job,
        fileName,
        uploadProgress,
        totalProgress,
        error,
        start,
        reset,
        markPaid: setJob,
    };
}
